import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { api, API, errMsg } from "@/lib/api";
import { useAuth, isAdmin } from "@/context/AuthContext";

const CHECKOUT_INTENT_KEY = "vceo_checkout_intent";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/plans/public`);
      setPlans(data);
    } catch (e) { toast.error(errMsg(e)); setPlans([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // After returning from signup/login, auto-resume checkout
  useEffect(() => {
    if (!user) return;
    const raw = sessionStorage.getItem(CHECKOUT_INTENT_KEY);
    if (!raw) return;
    sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
    const intent = JSON.parse(raw);
    if (!user.company_id) {
      // Needs a workspace first; stash it again and redirect to workspace create
      sessionStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
      navigate("/workspace");
      return;
    }
    if (!isAdmin(user)) {
      toast.error("Only owners and admins can start the subscription.");
      return;
    }
    startCheckout(intent.lookup_key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const startCheckout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  const choose = (lookup_key) => {
    if (user && user.company_id && isAdmin(user)) return startCheckout(lookup_key);
    sessionStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify({ lookup_key, at: Date.now() }));
    navigate(user ? (user.company_id ? "/settings?tab=billing" : "/workspace") : "/signup?next=pricing");
  };

  const priceFor = (plan) => (plan.prices || []).find((p) => p.interval === interval) || plan.prices?.[0];
  const monthlyEq = (plan) => {
    const yearly = plan.prices?.find((p) => p.interval === "year");
    return yearly ? yearly.amount / 12 : null;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="max-w-[1200px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="pricing-home-link">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Briefcase className="h-4 w-4 text-white" /></div>
          <span className="font-display font-semibold text-lg">Virtual CEO</span>
        </Link>
        <div className="flex gap-2">
          {user ? (
            <Button onClick={() => navigate(user.company_id ? "/dashboard" : "/workspace")} data-testid="pricing-dashboard-button">Open app</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="pricing-login-button">Sign in</Button>
              <Button onClick={() => navigate("/signup")} data-testid="pricing-signup-button">Get started</Button>
            </>
          )}
        </div>
      </header>

      <section className="hero-gradient noise">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <p className="inline-flex items-center gap-2 text-xs font-medium bg-card border border-border rounded-full px-3 py-1 mb-5">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> 14-day free trial on every paid plan
          </p>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl mx-auto leading-tight">
            Hire your full AI leadership team &mdash; pick the plan that fits your business.
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Transparent pricing, cancel anytime, every AI action still needs your approval.
          </p>
          <div className="mt-8 flex justify-center">
            <Tabs value={interval} onValueChange={setInterval}>
              <TabsList>
                <TabsTrigger value="month" data-testid="pricing-interval-month">Monthly</TabsTrigger>
                <TabsTrigger value="year" data-testid="pricing-interval-year">Yearly &middot; save ~17%</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 -mt-10 pb-16" data-testid="pricing-grid">
        {plans === null ? (
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {[1, 2].map((i) => <Skeleton key={i} className="h-[480px] w-full rounded-2xl" />)}
          </div>
        ) : plans.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-10 text-center max-w-xl mx-auto">
            <p className="font-display font-semibold text-lg">Pricing coming soon</p>
            <p className="text-sm text-muted-foreground mt-2">We're finalizing our plans. Start a free workspace and we'll let you know.</p>
            <Button className="mt-5" onClick={() => navigate("/signup")}>Create free workspace</Button>
          </div>
        ) : (
          <div className={`grid gap-5 ${plans.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2 max-w-3xl mx-auto"}`}>
            {plans.map((p, idx) => {
              const price = priceFor(p);
              const highlight = idx === 1 || (plans.length === 2 && idx === 1); // middle/second plan highlighted
              const mo = monthlyEq(p);
              return (
                <div key={p.plan_id}
                  className={`relative bg-card border rounded-2xl p-6 sm:p-7 flex flex-col ${highlight ? "border-primary shadow-xl ring-1 ring-primary/20" : "border-border"}`}
                  data-testid="pricing-plan-card">
                  {highlight && (
                    <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[11px] font-medium rounded-full px-3 py-1">Most popular</span>
                  )}
                  <p className="font-display text-2xl font-semibold" data-testid="pricing-plan-name">{p.name}</p>
                  <p className="text-sm text-muted-foreground min-h-[3rem] mt-1">{p.description}</p>
                  <div className="mt-5 flex items-baseline gap-1.5">
                    <span className="text-4xl font-semibold" data-testid="pricing-plan-amount">
                      {price ? `${(price.currency || "usd").toUpperCase()} ${Number(price.amount).toFixed(0)}` : "\u2014"}
                    </span>
                    {price?.interval && <span className="text-sm text-muted-foreground">/ {price.interval}</span>}
                  </div>
                  {interval === "year" && mo && (
                    <p className="text-xs text-muted-foreground mt-1">That's ~ USD {mo.toFixed(0)}/month, billed yearly.</p>
                  )}
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button className="mt-6 w-full"
                    variant={highlight ? "default" : "secondary"}
                    size="lg"
                    disabled={!price || busy === price?.lookup_key}
                    onClick={() => price && choose(price.lookup_key)}
                    data-testid={`pricing-choose-${p.name.toLowerCase()}`}>
                    {busy === price?.lookup_key ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                    {user ? "Start subscription" : "Start free trial"}
                    <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3 text-center">14-day free trial &middot; cancel anytime</p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pb-20">
        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <p className="font-display font-semibold text-xl">Not sure yet? Try the full product free for 14 days.</p>
            <p className="text-sm text-muted-foreground mt-2">Every new workspace starts on a free trial with the full AI leadership team. No card needed to try.</p>
          </div>
          <div className="md:text-right">
            <Button size="lg" onClick={() => navigate(user ? (user.company_id ? "/dashboard" : "/workspace") : "/signup")} data-testid="pricing-trial-cta">Start free</Button>
          </div>
        </div>
      </section>

      <footer className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 text-xs text-muted-foreground flex items-center justify-between">
        <span>&copy; {new Date().getFullYear()} Virtual CEO</span>
        <Link to="/" className="hover:underline">Home</Link>
      </footer>
    </div>
  );
}
